import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import Label from "src/components/Label";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import TrackerDetailOverviewTab from "./TrackerDetailOverviewTab";
import TrackerDetailInfoTab from "./TrackerDetailInfoTab";
import TrackerDetailPeopleTab from "./TrackerDetailPeopleTab";
import TrackerDetailSubmissionsTab from "./TrackerDetailSubmissionsTab";
import TrackerDetailAutomationsTab from "./TrackerDetailAutomationsTab";
import {
  getTrackerDetailApi,
  duplicateTrackerApi,
  archiveTrackerApi,
  deleteTrackerApi,
} from "src/DAL/tracker/tracker";
import {
  ensureTrackerShape,
  isLeadCaptureTracker,
  normalizeTracker,
  TRACKER_STATUS,
} from "./trackerStorage";
import {
  countUniquePeople,
  loadSubmissionsFromApi,
} from "./submissionStorage";
import { loadAutomationsFromApi } from "./automations/automationStorage";
import { backTarget } from "./trackerNav";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const formatTabLabel = (label, count) =>
  count == null ? label : `${label} (${count})`;

const JOURNEY_DETAIL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "details", label: "Tracker details" },
  { id: "people", label: "People", countKey: "people" },
  { id: "submissions", label: "Submissions", countKey: "submissions" },
  { id: "automations", label: "Automations", countKey: "automations" },
];

const LEAD_DETAIL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "details", label: "Tracker details" },
  { id: "people", label: "Leads", countKey: "people" },
  { id: "submissions", label: "Submissions", countKey: "submissions" },
  { id: "automations", label: "Automations", countKey: "automations" },
];

export default function TrackerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Arrow: back to the submission / review / list that opened this tracker; "All trackers" stays the list.
  const back = backTarget(location, navigate, "/trackers", "All trackers");
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [tracker, setTracker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDelete, setOpenDelete] = useState(false);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [tabCounts, setTabCounts] = useState({
    people: null,
    submissions: null,
    automations: null,
  });
  const actionsOpen = Boolean(actionsAnchor);

  const tabParam = searchParams.get("tab") || "overview";
  const isLead = isLeadCaptureTracker(tracker);
  const DETAIL_TABS = isLead ? LEAD_DETAIL_TABS : JOURNEY_DETAIL_TABS;
  const activeTab = DETAIL_TABS.some((t) => t.id === tabParam)
    ? tabParam
    : "overview";

  const setActiveTab = (nextTab) => {
    const next = new URLSearchParams(searchParams);
    if (nextTab === "overview") next.delete("tab");
    else next.set("tab", nextTab);
    setSearchParams(next, { replace: true });
  };

  const closeActionsMenu = () => setActionsAnchor(null);

  const fetchTrackerDetail = useCallback(async () => {
    let message = "Tracker not found";
    try {
      const res = await getTrackerDetailApi(id);
      if (res?.code === 200 && res.tracker) {
        setTracker(ensureTrackerShape(normalizeTracker(res.tracker)));
        return;
      }
      if (res?.message) message = res.message;
    } catch (e) {
      message = "Could not load tracker";
    } finally {
      setLoading(false);
    }

    enqueueSnackbar(message, { variant: "error" });
    navigate("/trackers", { replace: true });
  }, [id, enqueueSnackbar, navigate]);

  useEffect(() => {
    fetchTrackerDetail();
  }, [fetchTrackerDetail]);

  const trackerKey = tracker?.id || tracker?._id || "";
  useEffect(() => {
    if (!trackerKey) return undefined;
    let cancelled = false;
    setTabCounts({ people: null, submissions: null, automations: null });
    (async () => {
      const [subs, autos] = await Promise.all([
        loadSubmissionsFromApi(trackerKey, { limit: 500 }),
        loadAutomationsFromApi(trackerKey),
      ]);
      if (cancelled) return;
      const rows = subs?.rows || [];
      setTabCounts({
        people: countUniquePeople(rows),
        submissions: subs?.total ?? rows.length,
        automations: (autos?.automations || []).length,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [trackerKey]);

  const statusLabel = useMemo(() => {
    if (!tracker) return "Draft";
    if (
      tracker.status === TRACKER_STATUS.LIVE ||
      tracker.status === "published"
    ) {
      return "Live";
    }
    if (tracker.status === TRACKER_STATUS.ARCHIVED) return "Archived";
    return "Draft";
  }, [tracker]);

  const statusColor = useMemo(() => {
    if (statusLabel === "Live") return "success";
    if (statusLabel === "Archived") return "default";
    return "warning";
  }, [statusLabel]);

  const editHref = `/trackers/edit/${tracker?.id || tracker?._id || id}`;

  const handleDuplicate = async () => {
    closeActionsMenu();
    try {
      const res = await duplicateTrackerApi(tracker.id || tracker._id);
      if (res?.code === 200 && res.tracker) {
        const copy = normalizeTracker(res.tracker);
        enqueueSnackbar(
          isLeadCaptureTracker(copy)
            ? "Lead capture duplicated"
            : "Journey duplicated",
          { variant: "success" }
        );
        navigate(`/trackers/${copy.id}`);
        return;
      }
      enqueueSnackbar(res?.message || "Could not duplicate tracker", {
        variant: "error",
      });
    } catch (e) {
      enqueueSnackbar("Could not duplicate tracker", { variant: "error" });
    }
  };

  const handleArchive = async () => {
    closeActionsMenu();
    if (tracker.status === TRACKER_STATUS.ARCHIVED) {
      enqueueSnackbar("Already archived", { variant: "info" });
      return;
    }
    try {
      const res = await archiveTrackerApi(tracker.id || tracker._id);
      if (res?.code === 200 && res.tracker) {
        setTracker(ensureTrackerShape(normalizeTracker(res.tracker)));
        enqueueSnackbar("Journey archived — intake stopped", {
          variant: "success",
        });
        return;
      }
      enqueueSnackbar(res?.message || "Could not archive tracker", {
        variant: "error",
      });
    } catch (e) {
      enqueueSnackbar("Could not archive tracker", { variant: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      const res = await deleteTrackerApi(tracker.id || tracker._id);
      if (res?.code !== 200) {
        enqueueSnackbar(res?.message || "Could not delete tracker", {
          variant: "error",
        });
        return;
      }
    } catch (e) {
      enqueueSnackbar("Could not delete tracker", { variant: "error" });
      return;
    }
    setOpenDelete(false);
    enqueueSnackbar("Tracker deleted", { variant: "success" });
    navigate("/trackers");
  };

  if (loading && !tracker) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress size={36} sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!tracker) return null;

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Are you sure you want to delete this Tracker?"
        handleAgree={handleDelete}
      />

      <div className="container-fluid tracker-module-page">
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 2.5,
          }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <IconButton
                size="small"
                onClick={back.go}
                aria-label={back.label}
                sx={{ p: 0.75 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography
                component="button"
                type="button"
                onClick={() => navigate("/trackers")}
                sx={{
                  border: 0,
                  background: "none",
                  p: 0,
                  cursor: "pointer",
                  color: MUTED,
                  fontSize: 13,
                  "&:hover": { color: PRIMARY },
                }}
              >
                All trackers
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: 13 }}>/</Typography>
              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: 13,
                  fontWeight: 600,
                  maxWidth: 360,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {tracker.name ||
                  (isLead ? "Untitled lead capture" : "Untitled journey")}
              </Typography>
              <Label variant="ghost" color={statusColor}>
                {statusLabel}
              </Label>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 20, md: 22 },
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              {tracker.name ||
                (isLead ? "Untitled lead capture" : "Untitled journey")}
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: 13.5, color: MUTED }}>
              {isLead ? "Lead Capture · Standard journey" : "Journey"}
              {tracker.updatedAt
                ? ` · updated ${moment(tracker.updatedAt).format(
                    "DD MMM YYYY HH:mm"
                  )}`
                : ""}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            {/* Coach workspace 1.8 / 5.4 — added button; Edit unchanged. */}
            {!isLead ? (
              <button
                type="button"
                className="small-contained-outlined-button"
                onClick={() => navigate(`/trackers/${tracker.id || tracker._id}/check-in-rules`)}
              >
                Check-in rules
              </button>
            ) : null}
            <button
              type="button"
              className="small-contained-button"
              onClick={() => navigate(editHref)}
            >
              <EditOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Edit
            </button>
            <IconButton
              size="small"
              aria-label="More actions"
              aria-controls={actionsOpen ? "tracker-detail-actions" : undefined}
              aria-haspopup="true"
              aria-expanded={actionsOpen ? "true" : undefined}
              onClick={(e) => setActionsAnchor(e.currentTarget)}
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: "8px",
                bgcolor: "#fff",
                "&:hover": { bgcolor: PRIMARY_SOFT, borderColor: PRIMARY },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              id="tracker-detail-actions"
              anchorEl={actionsAnchor}
              open={actionsOpen}
              onClose={closeActionsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 0.75,
                  minWidth: 180,
                  borderRadius: "10px",
                  border: `1px solid ${BORDER}`,
                  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  closeActionsMenu();
                  setActiveTab("submissions");
                }}
              >
                <ListItemIcon>
                  <ListAltOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Submissions</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  closeActionsMenu();
                  handleDuplicate();
                }}
              >
                <ListItemIcon>
                  <ContentCopyOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Duplicate</ListItemText>
              </MenuItem>
              <MenuItem
                disabled={tracker.status === TRACKER_STATUS.ARCHIVED}
                onClick={() => {
                  closeActionsMenu();
                  handleArchive();
                }}
              >
                <ListItemIcon>
                  <ArchiveOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Archive</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  closeActionsMenu();
                  setOpenDelete(true);
                }}
                sx={{ color: "#dc2626" }}
              >
                <ListItemIcon sx={{ color: "#dc2626" }}>
                  <DeleteOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, nextTab) => setActiveTab(nextTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2.5,
            minHeight: 44,
            borderBottom: `1px solid ${BORDER}`,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              minHeight: 44,
              color: MUTED,
            },
            "& .Mui-selected": { color: `${PRIMARY} !important` },
            "& .MuiTabs-indicator": { backgroundColor: PRIMARY, height: 3 },
          }}
        >
          {DETAIL_TABS.map((t) => (
            <Tab
              key={t.id}
              value={t.id}
              label={formatTabLabel(
                t.label,
                t.countKey ? tabCounts[t.countKey] : null
              )}
            />
          ))}
        </Tabs>

        {activeTab === "overview" ? (
          <TrackerDetailOverviewTab tracker={tracker} />
        ) : null}
        {activeTab === "details" ? (
          <TrackerDetailInfoTab
            tracker={tracker}
            onTrackerUpdated={(updated) =>
              setTracker(ensureTrackerShape(normalizeTracker(updated)))
            }
          />
        ) : null}
        {activeTab === "people" ? (
          <TrackerDetailPeopleTab tracker={tracker} />
        ) : null}
        {activeTab === "submissions" ? (
          <TrackerDetailSubmissionsTab tracker={tracker} />
        ) : null}
        {activeTab === "automations" ? (
          <TrackerDetailAutomationsTab tracker={tracker} />
        ) : null}
      </div>
    </>
  );
}
